import { Company, Settings } from '@backend/domain'
import { CompanyRepository, SettingsRepository } from '@backend/repository'
import { FeedMetadata_ProcessingInstruction, Merchant, MerchantFeed } from '@core/google-reservations/types/feeds'
import { Processor, WorkerHost } from '@nestjs/bullmq'
import { Logger } from '@nestjs/common'
import { getUnixTime } from 'date-fns'
import SFTPClient from 'ssh2-sftp-client'
import { In } from 'typeorm'
import { TracedProcessor } from '../../../../../../common'
import { GOOGLE, Stage } from '../../../../../../config'
import { GOOGLE_RESERVATIONS_QUEUES, UploadMerchantFeedJob } from '../../../../../../core'

@Processor(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_MERCHANT_FEED)
export class UploadMerchantFeedProcessor extends WorkerHost {
	private readonly logger = new Logger(UploadMerchantFeedProcessor.name)

	private readonly sftpClient: SFTPClient

	private restaurants: Company[] = []
	private settingsMap: Map<string, Settings> = new Map()

	constructor(
		private readonly _companyRepository: CompanyRepository,
		private readonly _settingsRepository: SettingsRepository,
	) {
		super()

		this.sftpClient = new SFTPClient()
	}

	@TracedProcessor()
	async process(job: UploadMerchantFeedJob): Promise<void> {
		const { restaurantIds } = job.data

		await this.uploadFeed(restaurantIds)
	}

	private async uploadFeed(restaurantsIds: string[]): Promise<void> {
		try {
			await this.loadRestaurantData(restaurantsIds)

			const feed = await this.generateFeed()

			await this.sftpClient.connect({
				host: GOOGLE.ACTION_CENTER.FEEDS.URL,
				port: GOOGLE.ACTION_CENTER.FEEDS.PORT,
				privateKey: GOOGLE.ACTION_CENTER.FEEDS.SFTP_KEY,
				username: GOOGLE.ACTION_CENTER.FEEDS.USERNAMES.MERCHANTS,
			})

			await this.sftpClient.put(
				Buffer.from(JSON.stringify(feed)),
				`Merchant_Feed_${Stage}_${feed.metadata?.generation_timestamp}.json`,
			)

			this.logger.log({ message: 'Merchant feed uploaded', recordCount: feed.merchant?.length })
		} catch (error) {
			this.logger.error({ message: 'Failed to upload Merchant feed', error })
			throw error
		} finally {
			await this.sftpClient.end()
		}
	}

	private async generateFeed(): Promise<MerchantFeed> {
		const merchants: Merchant[] = []

		for (const restaurant of this.restaurants) {
			const settings = this.settingsMap.get(restaurant.id)

			if (!settings) {
				this.logger.warn({ message: 'Settings not found for tenant', tenantId: restaurant.id })
				continue
			}

			if (!settings.restaurant.address) {
				this.logger.warn({ message: 'Address not set for tenant', tenantId: restaurant.id })
				continue
			}

			const merchant: Merchant = {
				merchant_id: restaurant.id,
				name: restaurant.name,
				telephone: settings.restaurant.phone,
				geo: { unstructured_address: settings.restaurant.address },
				matching_hints: { place_id: settings.restaurant.googlePlaceId },
				category: 'restaurant',
			}

			merchants.push(merchant)
		}

		const feed: MerchantFeed = {
			metadata: {
				processing_instruction: FeedMetadata_ProcessingInstruction.PROCESS_AS_COMPLETE,
				total_shards: 1,
				shard_number: 0,
				generation_timestamp: getUnixTime(new Date()).toString(),
			},
			merchant: merchants,
		}
		return feed
	}

	private async loadRestaurantData(restaurantIds: string[]): Promise<void> {
		this.restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
		})

		const settings = await this._settingsRepository.find({
			where: { tenantId: In(restaurantIds) },
		})

		this.settingsMap = new Map(settings.map((setting) => [setting.tenantId as string, setting]))
	}
}
